import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { C, FONT, hexToRgba } from '../theme/tokens';

/**
 * Markdown for corp notes.
 *
 * Notes are the one place in this app where a member writes prose, and prose
 * about a wormhole chain is structured: a heading, a list of statics, a
 * warning quoted from comms, a fit pasted as code. Rendering that as one
 * undifferentiated paragraph was losing the structure the author typed.
 *
 * Hand-rolled rather than `react-markdown` + `remark-gfm` (~60kB gzipped)
 * because the surface is small — headings, lists, quotes, code, rules, and
 * inline emphasis — and because the app's own reference tokens (a J-code,
 * `/fitting/12`) have to linkify *inside* the markdown, which a stock
 * renderer needs a plugin to do.
 *
 * Everything renders as React elements. There is no `dangerouslySetInnerHTML`
 * anywhere in this file, and there must never be: these notes are written by
 * one member and read by the whole corp, so a note is untrusted input.
 *
 * ponytail: block-level parse is a single line-by-line pass, no AST. Nested
 * lists and tables are out; if a note ever genuinely needs them, that is the
 * point to reach for a real parser rather than growing this one.
 */

/** The three in-app references a note carries, linkified in place:
 *   J123456        -> that system's intel page
 *   /fitting/<id>  -> that fitting (any in-app absolute path, in fact)
 *   http(s)://...  -> external, new tab
 */
const AUTOLINK = /(J\d{6}\b|\/(?:fitting|system|intel)\/[A-Za-z0-9_-]+|https?:\/\/[^\s<>"')]+)/g;

/** Inline markdown, innermost first so `**bold**` wins over `*italic*`.
 *
 * The link arm allows one level of nested parentheses in the href. A plain
 * `[^)]+` stopped at the first `)`, which truncated any URL that contains
 * one — a Wikipedia article like `/wiki/Wormhole_(EVE)` lost its closing
 * bracket and left a stray `)` in the prose. */
const LINK = String.raw`\[[^\]]+\]\((?:[^()\s]|\([^()]*\))*\)`;
const INLINE = new RegExp(`(\`[^\`]+\`|\\*\\*[^*]+\\*\\*|__[^_]+__|\\*[^*]+\\*|_[^_]+_|~~[^~]+~~|${LINK})`, 'g');

/**
 * Where a `[label](href)` is allowed to point.
 *
 * The autolinker can only ever produce `http(s)://` or an in-app path, but
 * an explicit markdown link carries whatever the author typed — including
 * `javascript:`, which is script execution in a note one member writes and
 * the whole corp reads. Anything not on this list renders as plain text
 * rather than as a link, so a hostile href is visible instead of armed.
 *
 * `//evil.com` is rejected too: it starts with a slash, so it would
 * otherwise pass as an "in-app path" and then navigate off-site as a
 * protocol-relative URL.
 */
export function safeHref(href: string): { kind: 'internal' | 'external'; href: string } | null {
  const raw = href.trim();
  if (raw.startsWith('//')) return null;
  if (raw.startsWith('/')) return { kind: 'internal', href: raw };
  // Deliberately a prefix test on the lowercased string, not a URL parse:
  // `URL` accepts javascript: happily, and the question here is only which
  // of two schemes this is.
  const lower = raw.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return { kind: 'external', href: raw };
  return null;
}

const linkSx = {
  color: C.cyan,
  textDecoration: 'none',
  borderBottom: `1px solid ${hexToRgba(C.cyan, 0.35)}`,
  '&:hover': { borderBottomColor: C.cyan },
} as const;

const codeSx = {
  fontFamily: FONT.mono,
  fontSize: '0.92em',
  color: C.cyan,
  backgroundColor: hexToRgba(C.wormhole, 0.1),
  border: `1px solid ${C.hairline}`,
  borderRadius: '3px',
  px: 0.5,
  py: '1px',
} as const;

/** Autolinks a plain text run. Bottom of the recursion — no markdown left. */
function renderAutolinks(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(AUTOLINK).map((part, i) => {
    const key = `${keyPrefix}-a${i}`;
    // Odd indexes are the captured delimiters: String.split with a capturing
    // group interleaves them, so this needs no second match pass.
    if (i % 2 === 0) return <React.Fragment key={key}>{part}</React.Fragment>;

    if (/^J\d{6}$/.test(part)) {
      return (
        <Box component={Link} key={key} to={`/system/${part}`} sx={linkSx}>
          {part}
        </Box>
      );
    }
    if (part.startsWith('/')) {
      return (
        <Box component={Link} key={key} to={part} sx={linkSx}>
          {part}
        </Box>
      );
    }
    // noreferrer as well as noopener: an external link out of a corp tool
    // should not leak the vault URL in the Referer header.
    return (
      <Box component="a" key={key} href={part} target="_blank" rel="noopener noreferrer" sx={linkSx}>
        {part}
      </Box>
    );
  });
}

/** Inline emphasis, code and links, then autolinks inside what's left. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(INLINE).flatMap((part, i) => {
    const key = `${keyPrefix}-i${i}`;
    if (i % 2 === 0) return renderAutolinks(part, key);

    if (part.startsWith('`')) {
      return (
        <Box component="code" key={key} sx={codeSx}>
          {part.slice(1, -1)}
        </Box>
      );
    }
    if (part.startsWith('**') || part.startsWith('__')) {
      return (
        <Box component="strong" key={key} sx={{ fontWeight: 700, color: C.textBright }}>
          {renderInline(part.slice(2, -2), key)}
        </Box>
      );
    }
    if (part.startsWith('~~')) {
      return (
        <Box component="s" key={key} sx={{ color: C.textDim }}>
          {renderInline(part.slice(2, -2), key)}
        </Box>
      );
    }
    if (part.startsWith('*') || part.startsWith('_')) {
      return (
        <Box component="em" key={key} sx={{ fontStyle: 'italic' }}>
          {renderInline(part.slice(1, -1), key)}
        </Box>
      );
    }
    // [label](href) — an in-app path routes, http(s) opens externally, and
    // anything else is not a link at all (see safeHref).
    const match = /^\[([^\]]+)\]\((.*)\)$/s.exec(part);
    if (match) {
      const [, label, href] = match;
      const target = safeHref(href);
      if (!target) {
        return (
          <Box component="span" key={key} title={`Blocked link: ${href}`} sx={{ color: C.textDim }}>
            {label}
          </Box>
        );
      }
      return target.kind === 'internal' ? (
        <Box component={Link} key={key} to={target.href} sx={linkSx}>
          {label}
        </Box>
      ) : (
        <Box component="a" key={key} href={target.href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
          {label}
        </Box>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

type Block =
  | { kind: 'p' | 'quote'; lines: string[] }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'code'; lines: string[] }
  | { kind: 'hr' };

/** One line-by-line pass. Consecutive lines of the same kind coalesce, so a
 * list stays one <ul> and a paragraph keeps its soft line breaks. */
function parseBlocks(src: string): Block[] {
  const blocks: Block[] = [];
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  let fence: Block | null = null;
  // A blank line closes whatever block was open. Without this the "append to
  // the previous block if it is the same kind" rule below reached back across
  // the gap, so two paragraphs separated by a blank line rendered as one, and
  // two separate lists merged into a single list.
  let closed = false;

  for (const line of lines) {
    // A fence swallows everything verbatim until it closes — that is the
    // point of a fence, and it must be checked before any other rule.
    if (/^\s*```/.test(line)) {
      if (fence) {
        blocks.push(fence);
        fence = null;
      } else {
        fence = { kind: 'code', lines: [] };
      }
      continue;
    }
    if (fence) {
      fence.lines.push(line);
      continue;
    }

    if (!line.trim()) {
      closed = true;
      continue;
    }
    // `prev` is only a continuation candidate when no blank line intervened.
    const prev = closed ? undefined : blocks[blocks.length - 1];
    closed = false;
    if (/^\s*(?:---|\*\*\*|___)\s*$/.test(line)) {
      blocks.push({ kind: 'hr' });
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ kind: 'h', level: heading[1].length, text: heading[2] });
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet) {
      if (prev?.kind === 'ul') prev.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered) {
      if (prev?.kind === 'ol') prev.items.push(numbered[1]);
      else blocks.push({ kind: 'ol', items: [numbered[1]] });
      continue;
    }

    const quote = /^\s*>\s?(.*)$/.exec(line);
    if (quote) {
      if (prev?.kind === 'quote') prev.lines.push(quote[1]);
      else blocks.push({ kind: 'quote', lines: [quote[1]] });
      continue;
    }

    if (prev?.kind === 'p') prev.lines.push(line);
    else blocks.push({ kind: 'p', lines: [line] });
  }

  if (fence) blocks.push(fence); // unclosed fence still renders its content
  return blocks;
}

/** Headings step down in size but never change family: Rajdhani at label
 * scale, per the Uppercase-Means-Status rule — a note's heading is prose
 * structure, not a live readout, so it is not tracked-out uppercase. */
const HEADING_SIZE = ['1rem', '0.94rem', '0.88rem', '0.84rem'];

const Markdown: React.FC<{ text: string; dense?: boolean }> = ({ text, dense }) => {
  const blocks = parseBlocks(text);
  const bodySize = dense ? '0.82rem' : '0.88rem';

  return (
    <Box
      sx={{
        color: C.text,
        fontFamily: FONT.body,
        fontSize: bodySize,
        lineHeight: 1.6,
        // A pasted URL is one long unbreakable token; without this it sets
        // the container's min-content width and overflows the page.
        overflowWrap: 'anywhere',
        // Tight groups, generous separation: space above a block, never
        // below, so the first and last block sit flush to the container.
        '& > * + *': { mt: 1.25 },
      }}
    >
      {blocks.map((b, i) => {
        const key = `b${i}`;
        switch (b.kind) {
          case 'h':
            return (
              <Typography
                key={key}
                component={`h${Math.min(b.level + 2, 6)}` as 'h3'}
                sx={{
                  fontFamily: FONT.sub,
                  fontWeight: 700,
                  fontSize: HEADING_SIZE[b.level - 1],
                  color: C.textBright,
                  letterSpacing: '0.01em',
                  m: 0,
                  // More space above a heading than below it.
                  '&:not(:first-of-type)': { mt: 2 },
                }}
              >
                {renderInline(b.text, key)}
              </Typography>
            );
          case 'hr':
            return <Box key={key} sx={{ height: '1px', backgroundColor: C.hairline, border: 0 }} />;
          case 'code':
            return (
              <Box
                key={key}
                component="pre"
                sx={{
                  fontFamily: FONT.mono,
                  fontSize: '0.78rem',
                  color: C.cyan,
                  backgroundColor: hexToRgba(C.wormhole, 0.07),
                  border: `1px solid ${C.hairline}`,
                  borderRadius: '6px',
                  p: 1.25,
                  m: 0,
                  overflowX: 'auto',
                }}
              >
                {b.lines.join('\n')}
              </Box>
            );
          case 'quote':
            return (
              <Box
                key={key}
                sx={{
                  borderLeft: `1px solid ${hexToRgba(C.wormhole, 0.55)}`,
                  pl: 1.25,
                  color: C.textDim,
                  fontStyle: 'italic',
                }}
              >
                {renderInline(b.lines.join(' '), key)}
              </Box>
            );
          case 'ul':
          case 'ol':
            return (
              <Box
                key={key}
                component={b.kind}
                sx={{
                  m: 0,
                  pl: 2.5,
                  '& li': { mb: 0.4 },
                  '& li::marker': { color: C.wormhole },
                }}
              >
                {b.items.map((item, j) => (
                  <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                ))}
              </Box>
            );
          default:
            // Soft line breaks inside a paragraph are preserved: a scout
            // writing three lines about one hole meant three lines.
            return (
              <Typography key={key} sx={{ fontSize: 'inherit', lineHeight: 'inherit', m: 0, whiteSpace: 'pre-wrap' }}>
                {renderInline(b.lines.join('\n'), key)}
              </Typography>
            );
        }
      })}
    </Box>
  );
};

export default Markdown;
